/**
 * Living-bar line queue — SUMMARY VOICE ONLY (Fadi ruling 2026-07-30).
 *
 * The bar is the page's calm summary plus "Get the answers now"; QUESTIONS
 * live on the section cards and panel pills, never in the bar. This queue
 * used to rotate a channel-aware source opener and up to 3 page questions
 * ahead of the summary — three sources of variance that made the bar read
 * differently on every glance (question one moment, summary the next).
 *
 * The referrer-tailored source opener is retired FLEET-WIDE, not per-tenant:
 * it had already been hand-disabled on one tenant, and a channel-guessed
 * opener speaks to knowledge the AI does not have.
 *
 * Queue: generated invite/summary lines when present, else askAboutCopy.
 */
import type { TypewriterLine } from "./typewriter.ts";
import { askAboutCopy } from "./page-topic.ts";

export type BarQueueInput = {
  /** Generated invite/summary lines (PR-B); empty/absent → fallback askAboutCopy. */
  inviteLines?: string[];
  fallbackPlaceholder?: string;
};

export function buildBarQueue(input: BarQueueInput = {}): TypewriterLine[] {
  const out: TypewriterLine[] = [];

  const invites = (input.inviteLines ?? []).map((s) => s.trim()).filter(Boolean);
  if (invites.length > 0) {
    for (const t of invites.slice(0, 5)) {
      out.push({ kind: "invite", text: t });
    }
  } else {
    let fallback = input.fallbackPlaceholder || "Ask me anything";
    try {
      fallback = askAboutCopy(fallback);
    } catch {
      // Node/test env without document — use raw fallback.
    }
    out.push({ kind: "fallback", text: fallback });
  }

  // Dedup consecutive identical text
  const deduped: TypewriterLine[] = [];
  for (const line of out) {
    if (deduped.length && deduped[deduped.length - 1]!.text === line.text) continue;
    deduped.push(line);
  }
  return deduped.length ? deduped : [{ kind: "fallback", text: "Ask me anything" }];
}
